import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { pennerManagementApi } from "../../../services/api/omni/pennermanagement";
import {
  buildPennerCacheKey,
  getPennerCache,
} from "../../../services/api/omni/pennerManagementCache";

const SEGMENT_COLORS = [
  "#9e9e9e",
  "#42a5f5",
  "#1976d2",
  "#1565c0",
  "#0d47a1",
  "#1a237e",
];

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value));
};

const formatDateLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const formatPeriod = (start, end) => {
  if (!start && !end) return "";
  if (!start) return `bis ${formatDateLabel(end)}`;
  if (!end) return `ab ${formatDateLabel(start)}`;
  return `${formatDateLabel(start)} - ${formatDateLabel(end)}`;
};

const wavePriceLabel = (point) => {
  if (point.price != null) return formatCurrency(point.price);
  if (point.min_price != null && point.max_price != null) {
    return `${formatCurrency(point.min_price)} - ${formatCurrency(point.max_price)}`;
  }
  if (point.min_price != null) return formatCurrency(point.min_price);
  if (point.max_price != null) return formatCurrency(point.max_price);
  return "-";
};

const compareByDate = (left, right) =>
  new Date(left.date) - new Date(right.date);

const ArticleInsightsDialog = ({
  open,
  sku,
  articlename,
  articleType = "penner",
  onClose,
}) => {
  const isRenner = articleType === "renner";
  const waveNoun = isRenner ? "Preiserhöhung" : "Preisreduktion";
  const waveNounPlural = isRenner ? "Preiserhöhungen" : "Preisreduktionen";
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [channel, setChannel] = useState("");

  useEffect(() => {
    if (!open || !sku) return undefined;

    let cancelled = false;
    const cacheKey = buildPennerCacheKey("insights", {
      sku,
      article_type: articleType,
    });
    const cached = getPennerCache(cacheKey);
    if (cached) {
      setInsights(cached);
      const channels = [
        ...new Set((cached?.price_timeline ?? []).map((p) => p.channel)),
      ];
      setChannel(channels[0] ?? "");
    } else {
      setInsights(null);
    }

    (async () => {
      try {
        if (!cached) {
          setLoading(true);
        }
        const response = await pennerManagementApi.getArticleInsights(sku, {
          article_type: articleType,
        });
        if (!cancelled) {
          setInsights(response.data);
          const channels = [
            ...new Set(
              (response.data?.price_timeline ?? []).map((p) => p.channel),
            ),
          ];
          setChannel(channels[0] ?? "");
        }
      } catch (err) {
        console.error("Failed to load article insights", err);
        if (!cancelled) setInsights(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, sku, articleType]);

  const pricePoints = useMemo(() => {
    const timeline = (insights?.price_timeline ?? [])
      .filter((p) => p.channel === channel)
      .sort(compareByDate);
    return timeline.map((p) => ({
      ...p,
      label:
        p.wave === 0
          ? isRenner
            ? "Basis"
            : "Original"
          : isRenner
            ? `Erhöhung ${p.wave}`
            : `Welle ${p.wave}`,
      displayPrice: p.price ?? p.min_price ?? p.max_price ?? null,
    }));
  }, [insights, channel, isRenner]);

  const salesPoints = insights?.sales_timeline ?? [];
  const salesSegments = insights?.sales_segments ?? [];

  const segmentSeries = useMemo(() => {
    const waves = [
      ...new Set(salesPoints.map((point) => point.segment_wave ?? 0)),
    ].sort((a, b) => a - b);

    return waves.map((wave) => {
      const segmentMeta = salesSegments.find(
        (segment) => segment.wave === wave,
      );
      return {
        wave,
        label: segmentMeta?.label ?? `Phase ${wave}`,
        color: SEGMENT_COLORS[wave % SEGMENT_COLORS.length],
        data: salesPoints.map((point) =>
          (point.segment_wave ?? 0) === wave ? point.quantity : 0,
        ),
      };
    });
  }, [salesPoints, salesSegments]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Verlauf: {sku}
        {articlename ? ` - ${articlename}` : ""}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !insights ? (
          <Typography color="text.secondary">Keine Daten verfügbar.</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4, pb: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Verkäufe vor und nach {waveNounPlural} (letzte 365 Tage)
              </Typography>
              {salesPoints.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Keine Verkäufe im gewählten Zeitraum.
                </Typography>
              ) : (
                <>
                  <BarChart
                    height={240}
                    xAxis={[
                      {
                        data: salesPoints.map((p) =>
                          formatDateLabel(p.order_date),
                        ),
                        scaleType: "band",
                      },
                    ]}
                    series={segmentSeries.map((segment) => ({
                      data: segment.data,
                      label: segment.label,
                      color: segment.color,
                      stack: "sales",
                      valueFormatter: (value) => `${value} Stk.`,
                    }))}
                  />
                  <Box component="ul" sx={{ mt: 1, pl: 2, m: 0 }}>
                    {salesSegments.map((segment) => (
                      <Typography
                        component="li"
                        variant="caption"
                        key={`segment-${segment.wave}`}
                      >
                        {segment.label} (
                        {formatPeriod(segment.start_date, segment.end_date)}):{" "}
                        {segment.quantity} Stk.
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </Box>

            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
              >
                <Typography variant="subtitle2">
                  Preise bei {waveNounPlural}
                </Typography>
                {channel && (
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Kanal</InputLabel>
                    <Select
                      label="Kanal"
                      value={channel}
                      onChange={(e) => setChannel(e.target.value)}
                    >
                      {[
                        ...new Set(
                          (insights.price_timeline ?? []).map((p) => p.channel),
                        ),
                      ].map((ch) => (
                        <MenuItem key={ch} value={ch}>
                          {ch.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              {pricePoints.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Keine {waveNoun}-Daten.
                </Typography>
              ) : (
                <>
                  <LineChart
                    height={220}
                    xAxis={[
                      {
                        data: pricePoints.map((p) => formatDateLabel(p.date)),
                        scaleType: "point",
                      },
                    ]}
                    series={[
                      {
                        data: pricePoints.map((p) => p.displayPrice),
                        label: "Preis (EUR)",
                        curve: "stepAfter",
                        color: "#ed6c02",
                      },
                    ]}
                  />
                  <Box component="ul" sx={{ mt: 1, pl: 2, m: 0 }}>
                    {pricePoints.map((p) => (
                      <Typography
                        component="li"
                        variant="caption"
                        key={`${p.wave}-${p.channel}-${p.date}`}
                      >
                        {formatDateLabel(p.date)} - {p.label}:{" "}
                        {wavePriceLabel(p)}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ArticleInsightsDialog;
